/**
 * Listing Card - Display a marketplace listing in a grid
 */

import { Link } from 'react-router-dom';
import { MapPin, ImageOff, Clock, Tag } from 'lucide-react';
import type { MarketplaceListing } from '../../services/api';
import ConditionBadge from './ConditionBadge';
import AssemblyStatusBadge from './AssemblyStatusBadge';
import { useLanguage } from '../../contexts/LanguageContext';

interface Props {
  listing: MarketplaceListing & { distance_km?: number };
  showActions?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onMarkSold?: () => void;
}

export default function ListingCard({ listing, showActions, onEdit, onDelete, onMarkSold }: Props) {
  const { t } = useLanguage();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('common.time.today', 'Today');
    if (diffDays === 1) return t('common.time.yesterday', 'Yesterday');
    if (diffDays < 7) return t('common.time.daysAgo', '{days} days ago').replace('{days}', String(diffDays));
    if (diffDays < 30) return t('common.time.weeksAgo', '{weeks} weeks ago').replace('{weeks}', String(Math.floor(diffDays / 7)));
    return date.toLocaleDateString();
  };

  const discountPercent = listing.retail_price
    ? Math.round((1 - listing.asking_price / listing.retail_price) * 100)
    : null;

  const mainImage = listing.photos?.[0];

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    sold: 'bg-gray-100 text-gray-700',
    expired: 'bg-red-100 text-red-700',
    removed: 'bg-gray-100 text-gray-500',
  };

  return (
    <div className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-primary-300 transition-all duration-200">
      {/* Image */}
      <Link to={`/marketplace/listing/${listing.id}`} className="block">
        <div className="relative aspect-square bg-gray-100">
          {mainImage ? (
            <img
              src={mainImage}
              alt={listing.product_code}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageOff className="w-12 h-12 text-gray-300" />
            </div>
          )}

          {/* Discount Badge */}
          {discountPercent && discountPercent > 0 && (
            <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
              <Tag className="w-3 h-3" />
              {t('listingCard.discount', '{percent}% OFF').replace('{percent}', String(discountPercent))}
            </div>
          )}

          {/* Status Badge (for own listings) */}
          {showActions && listing.status !== 'active' && (
            <div className={`absolute top-2 left-2 px-2 py-1 text-xs font-medium rounded-full ${statusColors[listing.status]}`}>
              {listing.status.charAt(0).toUpperCase() + listing.status.slice(1)}
            </div>
          )}

          {/* Photo count */}
          {listing.photos && listing.photos.length > 1 && (
            <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 text-white text-xs rounded">
              {t('listingCard.photoCount', '{count} photos').replace('{count}', String(listing.photos.length))}
            </div>
          )}
        </div>
      </Link>

      {/* Content */}
      <div className="p-3">
        <Link to={`/marketplace/listing/${listing.id}`}>
          {/* Product Code */}
          <p className="text-xs text-gray-500 mb-1">{listing.product_code}</p>

          {/* Price */}
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-xl font-bold text-gray-900">
              {formatPrice(listing.asking_price)}
            </span>
            {listing.retail_price && listing.retail_price > listing.asking_price && (
              <span className="text-sm text-gray-400 line-through">
                {formatPrice(listing.retail_price)}
              </span>
            )}
          </div>

          {/* Condition & Assembly */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ConditionBadge condition={listing.condition} />
            <AssemblyStatusBadge status={listing.assembly_status} />
          </div>

          {/* Location */}
          {(listing.neighborhood || listing.postal_code) && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
              <MapPin className="w-3 h-3" />
              <span className="truncate">
                {listing.neighborhood || listing.postal_code}
                {listing.distance_km !== undefined && listing.distance_km !== null && (
                  <span className="ml-1">({listing.distance_km.toFixed(1)} {t('listingCard.distanceUnit', 'km')})</span>
                )}
              </span>
            </div>
          )}

          {/* Posted date */}
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <Clock className="w-3 h-3" />
            <span>{formatDate(listing.created_at)}</span>
          </div>
        </Link>

        {/* Actions for own listings */}
        {showActions && listing.status === 'active' && (
          <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={onEdit}
              className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t('listingCard.edit', 'Edit')}
            </button>
            <button
              onClick={onMarkSold}
              className="flex-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
            >
              {t('listingCard.markSold', 'Mark Sold')}
            </button>
            <button
              onClick={onDelete}
              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
            >
              {t('listingCard.delete', 'Delete')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
